import { focusSafely } from '@react-aria/focus';
import { type ForwardedRef, useRef } from 'react';
import { Button } from '../button/button.js';
import { IconButton } from '../button/icon-button.js';
import type { FocusableElement } from '../types.js';
import { BasePagination, type BasePaginationProps } from './base-pagination.js';

/** @internal */
export interface ActionPaginationProps extends BasePaginationProps {
  navRef?: ForwardedRef<HTMLElement>;
  /** A callback function that is called when the page number changes. */
  onPageChange: (page: number) => void;
}

/** @internal */
export function ActionPagination({ currentPage, pageCount, onPageChange, ...props }: ActionPaginationProps) {
  const listRef = useRef<HTMLUListElement>(null);

  return (
    <BasePagination
      {...props}
      listRef={listRef}
      currentPage={currentPage}
      pageCount={pageCount}
      renderPreviousButton={(previousButtonProps) => (
        <IconButton
          {...previousButtonProps}
          onPress={() => {
            if (currentPage === 2) {
              // "Previous" button will become disabled after page change, so move focus to the first page button
              focusSafely(listRef.current?.querySelector(`[data-first]`) as FocusableElement);
            }

            onPageChange(currentPage - 1);
          }}
        />
      )}
      renderNextButton={(nextButtonProps) => (
        <IconButton
          {...nextButtonProps}
          onPress={() => {
            if (currentPage === pageCount - 1) {
              // "Next" button will become disabled after page change, so move focus to the last page button
              focusSafely(listRef.current?.querySelector(`[data-last]`) as FocusableElement);
            }

            onPageChange(currentPage + 1);
          }}
        />
      )}
      renderPageButton={({ pageNumber, isCurrentPage, pageButtonProps }) => (
        <Button
          {...pageButtonProps}
          aria-current={isCurrentPage || undefined}
          onPress={() => onPageChange(pageNumber)}
        />
      )}
    />
  );
}
